import { NgTemplateOutlet, isPlatformBrowser } from '@angular/common';
import {
  Component,
  DestroyRef,
  OnDestroy,
  PLATFORM_ID,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DomSanitizer } from '@angular/platform-browser';
import { RouterLink } from '@angular/router';
import { BigSaleBanner } from '../../../shared/big-sale-banner/big-sale-banner';
import { categoryFallbackMark, categoryInitials } from '../../../shared/category-icon/category-icon';
import { LatestInsights } from '../../../shared/latest-insights/latest-insights';
import { ProductCard } from '../../../shared/product-card/product-card';
import { StoreLogo } from '../../../shared/store-logo/store-logo';
import { AutoScrollRow } from '../../../shared/auto-scroll-row/auto-scroll-row';
import { catchError, forkJoin, map, of, switchMap, tap } from 'rxjs';

import { SAVING_ARENA_API_CONFIG } from '../../../core/api/api.config';
import {
  FrontendCoupon,
  FrontendDealCategoryListItem,
  FrontendDealStore,
  FrontendEvent,
  FrontendHomeSettings,
  FrontendPopularCategory,
  FrontendProduct,
  FrontendStore,
} from '../../../core/api/api.models';
import { SavingArenaApiService } from '../../../core/api/saving-arena-api.service';
import { AssistantService } from '../../../core/assistant/assistant.service';
import { TrustedCmsHtmlPipe } from '../../../core/api/trusted-cms-html.pipe';
import { SeoService } from '../../../core/seo/seo.service';
import { webPageSchema } from '../../../core/seo/structured-data';
import { AffiliateLinkService } from '../../../core/affiliate/affiliate-link.service';
import { formatOfferCount } from '../../../core/format/offer-count';

// Dev/QA markers: a red dot flags homepage sections that still render hardcoded content
// not yet wired to the admin panel. Flip this to false to hide every marker globally.
const STATIC_SECTION_MARKERS_ENABLED = true;

// Max stores shown in the "Recently Partnered Stores" grid (4 columns × 4 rows in the design).
const PARTNERED_STORES_LIMIT = 16;
// The DB holds some same-brand records (region/network variants), so we over-fetch the newest
// stores and de-duplicate down to PARTNERED_STORES_LIMIT unique brands. 40 comfortably yields the
// 16 unique brands we need (the newest stores are almost all distinct brands) while keeping the
// payload small — fetching the full 100-store page was ~2.5x the bytes for no benefit.
const PARTNERED_STORES_FETCH_LIMIT = 40;
// Max cards in the "New Collection" grid — the backend returns one (newest) product per store.
const NEW_COLLECTION_LIMIT = 8;
// 10 fills two clean rows of the five-across Trending Coupons grid.
const TRENDING_COUPONS_LIMIT = 10;
// Eight, so the row fills whatever column count the viewport gets: two rows of four on a laptop,
// four rows of two on a phone. A full desktop shows six across in a single row, and the two spare
// cards are hidden there in CSS (`.sav-today-deals-cards`) rather than fetched conditionally —
// slicing by screen width would need the client to re-render what the server already sent.
const TODAY_DEALS_LIMIT = 8;

// Every homepage section that currently renders static content. Sections already driven by
// the admin panel (hero banner, brand directory) are intentionally absent — they get no dot.
type StaticHomeSection =
  | 'ai'
  | 'deals'
  | 'sponsoredBrands'
  | 'popularCategories'
  | 'trendingCoupons'
  | 'premiumCollection'
  | 'partneredStores'
  | 'bigSaleBanner'
  | 'journal'
  | 'faq';

type DealThumbType = 'airpods' | 'speaker';

interface DealItem {
  /** Product id, for the `/go/p/:id` "Buy Now" redirect. */
  id: string;
  title: string;
  discount: string;
  category: string;
  imageUrl: string;
  hoverImageUrl: string;
  /** Formatted current price, e.g. "$17.99". */
  price: string;
  /** Formatted pre-markdown price, '' when the product isn't discounted. */
  oldPrice: string;
  /** Whether the product goes off-site, so the card builds its own /go/p/ redirect. */
  isExternal: boolean;
  /** Route to this product's own page, '' when the product has no slug. */
  detailPath: string;
}

interface DealTab {
  id: string;
  label: string;
  presentedBy: string;
  logoUrl: string;
  /** This store's coupon page (`/<slug>`), for "View All Coupons". */
  couponsPath: string;
  /** This store's deals page (`/deals/<key>`), or '' when it has too few products for one. */
  dealsPath: string;
  deals: DealItem[];
}

type PopularCategoryThumb = 'men' | 'women' | 'accessories' | 'outerwear' | 'shoes';

interface PopularCategoryItem {
  label: string;
  thumb?: PopularCategoryThumb;
  slug?: string;
  /**
   * Where the tile links. Set per tab rather than assembled in the template: "Coupons & Offers"
   * points at `/categories/<slug>`, "Products & Deals" at `/collection/<slug>`.
   */
  path?: string;
  imageUrl?: string;
  /** A paintable glyph or the category's initials — never an unrenderable icon class. */
  iconGlyph?: string;
}

interface PopularCategoryTab {
  id: string;
  label: string;
  categories: PopularCategoryItem[];
}

interface ContentTab {
  id: string;
  label: string;
  /** Category slug, when the tab maps to one — lets a section link to that category's page. */
  slug?: string;
}

interface CouponItem {
  /** Coupon id — needed for the `/go/:store/:id` redirect and the offer popup's URL. */
  id: string;
  store: string;
  /** Store slug on its own, for building those two URLs. */
  storeSlug: string;
  /** Route to the store's own page (`/<storeSlug>`), or '' when the store has no slug. */
  storePath: string;
  logoUrl: string;
  title: string;
  expires: string;
  ctaType: 'code' | 'deal';
  codeSuffix?: string;
}

interface PremiumCollectionItem {
  /** Product id, for the `/go/p/:id` "Buy Now" redirect. */
  id: string;
  store: string;
  storeLogoUrl?: string;
  storePath: string;
  /** Route to this product's own page, '' when the product has no slug. */
  detailPath: string;
  category: string;
  title: string;
  description: string;
  /** Whether the product goes off-site, so the card builds its own /go/p/ redirect. */
  isExternal: boolean;
  price: string;
  oldPrice?: string;
  badge?: string;
  imageUrl: string;
  hoverImageUrl: string;
}

interface SponsoredBrandCard {
  title: string;
  description: string;
  imageUrl: string;
  storeName: string;
  storeLogoUrl?: string;
  storePath: string;
}

interface JournalPost {
  id: number;
  category: string;
  title: string;
  excerpt: string;
  author: string;
  date: string;
  readTime: string;
  imageUrl: string;
  storeLogoUrl: string;
}

interface FaqItem {
  question: string;
  answer: string;
}

interface PartneredStoreItem {
  name: string;
  logoUrl: string;
  couponsAndDealsCount: string;
  path: string;
}

type HeroTheme = 'teal' | 'slate' | 'indigo';
type ApiRecord = Record<string, unknown>;

interface HeroBadge {
  alt: string;
  /** External destination. Only used when `path` is null. */
  href: string;
  /** On-site route (`/<storeSlug>`) when the badge resolves to a store page, else null. */
  path: string | null;
  logoUrl: string;
}

interface HeroSlide {
  titleHtml: string;
  titleLines: string[];
  highlightWord: string;
  descriptionLines: string[];
  ctaLabel: string;
  ctaLink: string;
  storesTitle: string;
  storesSubtitle: string;
  theme: HeroTheme;
  imageUrl: string;
  badges: HeroBadge[];
}

interface BrandDirectoryStore {
  name: string;
  href: string;
  domain: string;
  logoUrl: string;
  coverUrl: string;
  coverBackgroundColor: string;
  description: string;
}

// A featured store shown as a circular logo under the "{Event} Top Stores" heading.
interface TopStoreLogo {
  id: string;
  name: string;
  logoUrl: string;
  path: string;
}

// One "{Event} Top Stores" block (heading + circular-logo row). The home page renders one
// block per currently-running event, so several live events show stacked with their own headings.
interface TopStoresSection {
  id: string;
  heading: string;
  stores: TopStoreLogo[];
}

@Component({
  selector: 'app-home-page',
  imports: [
    NgTemplateOutlet,
    RouterLink,
    LatestInsights,
    TrustedCmsHtmlPipe,
    ProductCard,
    BigSaleBanner,
    StoreLogo,
    AutoScrollRow,
  ],
  templateUrl: './home-page.html',
  styleUrl: './home-page.scss',
})
export class HomePage implements OnDestroy {
  private readonly api = inject(SavingArenaApiService);
  private readonly affiliate = inject(AffiliateLinkService);
  private readonly assistant = inject(AssistantService);
  private readonly apiConfig = inject(SAVING_ARENA_API_CONFIG);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly destroyRef = inject(DestroyRef);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly seo = inject(SeoService);
  /**
   * The homepage AI field. It shares one conversation with the drawer through `AssistantService`,
   * so submitting here opens the panel onto the same thread the visitor then continues in.
   */
  protected readonly assistantDraft = signal('');

  protected onAssistantDraft(value: string): void {
    this.assistantDraft.set(value);
  }

  /** Focusing the field opens the panel, per the brief — the visitor can then type in either one. */
  protected openAssistant(): void {
    this.assistant.open();
  }

  protected askAssistant(): void {
    const question = this.assistantDraft().trim();
    if (!question) {
      this.assistant.open();
      return;
    }
    this.assistant.ask(question);
    this.assistantDraft.set('');
  }

  private heroAutoplayTimer: ReturnType<typeof setInterval> | null = null;
  private popularCategoryAutoplayTimer: ReturnType<typeof setInterval> | null = null;

  protected readonly heroSlides = signal<HeroSlide[]>([]);
  protected readonly hasHeroSlides = computed(() => this.heroSlides().length > 0);
  protected readonly currentHeroSlideIndex = signal(0);
  protected readonly heroSlideDirection = signal<'next' | 'prev'>('next');
  protected readonly heroSlideAnimate = signal(true);
  protected readonly brandDirectoryTitle = signal('');
  protected readonly brandDirectoryStores = signal<BrandDirectoryStore[]>([]);
  protected readonly sponsoredBrandsTitle = signal('');
  protected readonly sponsoredBrands = signal<SponsoredBrandCard[]>([]);
  // Home FAQ section — admin-managed (Settings → Home Page → FAQ's). Empty until loaded.
  protected readonly homeFaqTitle = signal('');
  protected readonly homeFaqs = signal<FaqItem[]>([]);

  // "{Event} Top Stores" blocks — one per currently-running event (or the single next upcoming
  // one when none are live). Populated by loadTopStores(); empty until an event's stores load.
  protected readonly topStoresSections = signal<TopStoresSection[]>([]);
  // Name + link of the primary featured event (current, else next upcoming). Drives the "BIG SALE"
  // banner labels ("{Event} Deals" / "{Event} Coupons"). Empty until events load.
  protected readonly featuredEventName = signal('');
  protected readonly featuredEventPath = signal<string | null>(null);
  protected readonly currentHeroSlide = computed(
    () => this.heroSlides()[this.currentHeroSlideIndex()] ?? this.heroSlides()[0],
  );

  /**
   * The home page's H1 when the hero has no headline of its own.
   *
   * The H1 used to be conditional on admin-supplied hero copy, so a site whose hero carried an
   * image but no title — which is what an SEO crawl found here — served the home page with no H1 at
   * all. The most important page on the site cannot have its only H1 depend on a CMS field being
   * filled in.
   *
   * Worded as a description of the page rather than as a copy of the `<title>`, which reads as
   * duplication to both a reader and a crawler.
   */
  private readonly HERO_TITLE_FALLBACK = 'Verified Coupons, Promo Codes &amp; Deals';

  protected readonly heroTitleHtml = computed(() => {
    const slide = this.currentHeroSlide();

    if (!slide) {
      return this.HERO_TITLE_FALLBACK;
    }

    if (slide.titleHtml) {
      return this.sanitizer.bypassSecurityTrustHtml(this.flattenHeadingHtml(slide.titleHtml));
    }

    if (slide.titleLines.length === 0) {
      return this.HERO_TITLE_FALLBACK;
    }

    const escapedWord = this.escapeRegExp(slide.highlightWord);
    const heading = slide.titleLines.map((line) => this.escapeHtml(line)).join('<br />');

    if (!escapedWord) {
      return heading;
    }

    return heading.replace(
      new RegExp(`(${escapedWord})`, 'i'),
      '<span class="sav-hero-highlight">$1</span>',
    );
  });

  /**
   * Turns block-level tags in the admin's hero title into line breaks.
   *
   * The rich-text editor lets an admin write the headline as `<h2>` lines, and that HTML is
   * injected into the hero's `<h1>`. A heading (or `<p>`/`<div>`) nested inside an `<h1>` is invalid
   * HTML: the browser's parser closes the `<h1>` early and hoists the block out as a sibling. The
   * server-rendered tree and the browser tree then differ, hydration fails with NG0500, and the
   * whole hero stops responding to clicks — which is why the carousel dots did nothing.
   *
   * Dropping the block tags and joining the lines with `<br />` keeps the headline looking the same
   * (and keeps inline `<span>` colouring), while leaving the `<h1>` valid — so it also stops the
   * page shipping three stray `<h2>`s inside its `<h1>`.
   */
  private flattenHeadingHtml(html: string): string {
    return html
      .replace(/<(h[1-6]|p|div)\b[^>]*>/gi, '')
      .replace(/<\/(h[1-6]|p|div)\s*>/gi, '<br />')
      .replace(/(?:\s*<br\s*\/?>\s*)+$/i, '')
      .trim();
  }

  // "Recently Partnered Stores" — the newest published stores from the admin panel/DB.
  // Populated by loadPartneredStores(); empty until the API responds (which keeps the red dot on).
  protected readonly partneredStores = signal<PartneredStoreItem[]>([]);

  // Tabs are admin-flagged stores (showInTodayDeals); each shows that store's latest products.
  protected readonly dealTabs = signal<DealTab[]>([]);

  // Matches TODAY_DEALS_LIMIT, so a tab's deals all show at once instead of being split across a
  // second slide.
  protected readonly cardsPerSlide = TODAY_DEALS_LIMIT;
  protected readonly selectedTabId = signal('');
  protected readonly currentSlide = signal(0);
  protected readonly dealThumbFailedByKey = signal<Record<string, boolean>>({});

  // Static fallback used until the admin flags coupon categories for the "Coupons & Offers" tab.
  private readonly defaultPopularCategoryTabs: PopularCategoryTab[] = [
    {
      id: 'coupons-offers',
      label: 'Coupons & Offers',
      categories: [
        { label: 'Men', thumb: 'men' },
        { label: 'Women', thumb: 'women' },
        { label: 'Accessories', thumb: 'accessories' },
        { label: 'Outerwear', thumb: 'outerwear' },
        { label: 'Shoes', thumb: 'shoes' },
        { label: 'Beauty', thumb: 'accessories' },
        { label: 'Bags', thumb: 'women' },
        { label: 'Streetwear', thumb: 'men' },
      ],
    },
    {
      id: 'products-deals',
      label: 'Products & Deals',
      categories: [
        { label: 'Men', thumb: 'men' },
        { label: 'Women', thumb: 'women' },
        { label: 'Accessories', thumb: 'accessories' },
        { label: 'Outerwear', thumb: 'outerwear' },
        { label: 'Shoes', thumb: 'shoes' },
        { label: 'Electronics', thumb: 'accessories' },
        { label: 'Home', thumb: 'outerwear' },
        { label: 'Fitness', thumb: 'shoes' },
      ],
    },
  ];

  // Admin-flagged coupon categories ("Show in Home Page Popular Categories"). When non-empty they
  // replace the static "Coupons & Offers" tab items.
  private readonly popularCouponCategories = signal<PopularCategoryItem[]>([]);
  // Categories that currently have a /collection/:slug page. When non-empty they replace the
  // static "Products & Deals" tab items, so the tiles link to real deals pages.
  private readonly popularDealCategories = signal<PopularCategoryItem[]>([]);
  /**
   * Only the tabs that have real categories behind them.
   *
   * A tab with no data used to fall back to `defaultPopularCategoryTabs`, which is a set of
   * placeholder fixtures — "Men", "Women", "Outerwear" — that are not categories in the catalogue.
   * They carry no slug, so their tiles linked nowhere, and no artwork, so they rendered as five
   * blank circles. With the deals catalogue empty that is exactly what the "Products & Deals" tab
   * was showing. Dropping the tab shows nothing invented and leaves no empty cards; it comes back
   * on its own as soon as the admin has deals categories to put in it.
   */
  protected readonly popularCategoryTabs = computed<PopularCategoryTab[]>(() => {
    const byId: Record<string, PopularCategoryItem[]> = {
      'coupons-offers': this.popularCouponCategories(),
      'products-deals': this.popularDealCategories(),
    };

    return this.defaultPopularCategoryTabs
      .filter((tab) => (byId[tab.id] || []).length > 0)
      .map((tab) => ({ ...tab, categories: byId[tab.id] }));
  });

  /**
   * How many category cards one slide shows — matched to the column count at each width.
   *
   * This was a fixed 5 while `.sav-popular-categories-grid` steps 5 / 3 / 2 columns (styles.scss,
   * at 1100px and 720px). Below 1100px that put five cards into a three-column grid, so the last
   * two wrapped onto a row of their own with an empty slot beside them — the part-filled row the
   * site's grids are not supposed to end on. The slide size now follows the same two breakpoints,
   * so a slide is always exactly one full row.
   */
  private readonly viewportWidth = signal(0);
  private onViewportResize: (() => void) | null = null;

  protected readonly popularCardsPerSlide = computed(() => {
    const width = this.viewportWidth();
    // Server-side there is no viewport; render the widest layout, which is what the desktop-first
    // markup assumes.
    if (!width) return 5;
    if (width <= 720) return 2;
    if (width <= 1100) return 3;
    return 5;
  });
  protected readonly selectedPopularCategoryTabId = signal('coupons-offers');
  protected readonly currentPopularCategorySlide = signal(0);
  // Tabs and coupons come from the admin API: each tab is a CouponCategory flagged
  // "Show in Trending Coupons", and its coupons are that category's latest active coupons.
  protected readonly trendingCouponTabs = signal<ContentTab[]>([]);
  protected readonly selectedTrendingCouponTabId = signal('');
  protected readonly trendingCouponsByTab = signal<Record<string, CouponItem[]>>({});
  protected readonly visibleTrendingCoupons = computed(
    () => this.trendingCouponsByTab()[this.selectedTrendingCouponTabId()] ?? [],
  );
  protected readonly trendingMoreLabel = computed(() => {
    const tab = this.selectedTrendingCouponTab();
    return tab ? `View More ${tab.label} Coupons` : 'View More Coupons';
  });
  private readonly selectedTrendingCouponTab = computed(() =>
    this.trendingCouponTabs().find((item) => item.id === this.selectedTrendingCouponTabId()),
  );
  /** "View More …" opens the selected tab's category page; '' hides the link. */
  protected readonly trendingMorePath = computed(() => {
    const slug = this.selectedTrendingCouponTab()?.slug?.trim() ?? '';
    return slug ? `/categories/${slug}` : '';
  });
  // Tabs and products come from the admin API: each tab is a CouponCategory flagged
  // "Show in New Collection", and its products are the latest per store in that category.
  protected readonly premiumCollectionTabs = signal<ContentTab[]>([]);
  protected readonly selectedPremiumCollectionTabId = signal<string>('');
  private readonly premiumCollectionByCategory = signal<Record<string, PremiumCollectionItem[]>>(
    {},
  );
  // Products for the currently selected tab.
  protected readonly premiumCollectionItems = computed<PremiumCollectionItem[]>(
    () => this.premiumCollectionByCategory()[this.selectedPremiumCollectionTabId()] ?? [],
  );
  protected readonly journalTabs: ContentTab[] = [
    { id: 'all', label: 'All' },
    { id: 'technology', label: 'Technology' },
    { id: 'design', label: 'Design' },
    { id: 'strategy', label: 'Strategy' },
    { id: 'tutorials', label: 'Tutorials' },
  ];
  protected readonly selectedJournalTabId = signal(this.journalTabs[0].id);
  protected readonly journalPosts: JournalPost[] = [
    {
      id: 1,
      category: 'Technology',
      title: 'The Future of AI in Modern Software Development',
      excerpt:
        'Explore how generative AI is reshaping the way developers write, test, and deploy code in 2024.',
      author: 'Alex Rivers',
      date: 'Oct 12, 2023',
      readTime: '5 min read',
      imageUrl:
        'https://images.unsplash.com/photo-1677442136019-21780ecad995?auto=format&fit=crop&q=80&w=800',
      storeLogoUrl: '/images/top-stores/logo-adulttime.png',
    },
    {
      id: 2,
      category: 'Design',
      title: 'Minimalism: Why Less is More in UI/UX Design',
      excerpt:
        'Learn the principles of functional minimalism and how to create impactful interfaces with less clutter.',
      author: 'Sarah Chen',
      date: 'Oct 10, 2023',
      readTime: '4 min read',
      imageUrl:
        'https://images.unsplash.com/photo-1558655146-d09347e92766?auto=format&fit=crop&q=80&w=800',
      storeLogoUrl: '/images/top-stores/logo-amazon.png',
    },
    {
      id: 3,
      category: 'Strategy',
      title: 'Scaling Your Startup: From Seed to Series A',
      excerpt:
        'The critical milestones and cultural shifts required to successfully grow your business at speed.',
      author: 'Marcus Thorne',
      date: 'Oct 08, 2023',
      readTime: '8 min read',
      imageUrl:
        'https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&q=80&w=800',
      storeLogoUrl: '/images/top-stores/logo-rakuten.png',
    },
  ];
  protected readonly faqItems: FaqItem[] = [
    {
      question: 'How do I use a coupon code on SavingArena?',
      answer:
        "Browse our catalog of coupons and deals, click on the one you want to use, and copy the code. Then shop at the retailer's website and paste the code at checkout to apply your savings.",
    },
    {
      question: 'Are the coupons on SavingArena really free?',
      answer:
        'Yes! All coupons and deals on SavingArena are completely free to use. We partner with retailers to bring you exclusive discounts without any hidden fees or subscription requirements.',
    },
    {
      question: 'How often are the deals and coupons updated?',
      answer:
        'Our team refreshes deals daily to ensure you have access to the latest discounts. We remove expired codes automatically and add new ones as soon as they become available from our retail partners.',
    },
    {
      question: 'Do I need to create an account to use SavingArena?',
      answer:
        'No account is required. You can browse and copy coupon codes immediately. However, creating a free account gives you access to personalized deal alerts, price drop notifications, and a saved wishlist.',
    },
    {
      question: 'How does SavingArena make money?',
      answer:
        "We earn a small commission from retailers when you make a purchase using our links or codes. This doesn't affect the savings you get — in fact, it allows us to keep the service free for everyone.",
    },
    {
      question: "What should I do if a coupon code doesn't work?",
      answer:
        "If a code doesn't work, first double-check that you've entered it correctly. Some codes have minimum purchase requirements or exclusions. If it still fails, please report it so we can verify or replace it promptly.",
    },
  ];
  protected readonly expandedFaqIndex = signal<number | null>(null);
  protected readonly visibleJournalPosts = computed(() => {
    const tabId = this.selectedJournalTabId();

    if (tabId === 'all') {
      return this.journalPosts;
    }

    const selectedTab = this.journalTabs.find((tab) => tab.id === tabId);
    return this.journalPosts.filter((post) => post.category === selectedTab?.label);
  });

  protected readonly selectedTab = computed<DealTab | null>(
    () =>
      this.dealTabs().find((tab) => tab.id === this.selectedTabId()) ?? this.dealTabs()[0] ?? null,
  );

  protected readonly totalSlides = computed(() =>
    Math.max(1, Math.ceil((this.selectedTab()?.deals.length ?? 0) / this.cardsPerSlide)),
  );

  protected readonly slideIndexes = computed(() =>
    Array.from({ length: this.totalSlides() }, (_, index) => index),
  );

  protected readonly visibleDeals = computed(() => {
    const tab = this.selectedTab();
    if (!tab) {
      return [];
    }
    const start = this.currentSlide() * this.cardsPerSlide;
    return tab.deals.slice(start, start + this.cardsPerSlide);
  });

  /**
   * The tab on screen. Now that a tab without data is dropped rather than filled with placeholders,
   * the list can be empty and the selected id can name a tab that is no longer there, so this falls
   * back to the first remaining tab and finally to an empty one. The section itself is hidden in
   * that last case — see `hasPopularCategories`.
   */
  protected readonly selectedPopularCategoryTab = computed<PopularCategoryTab>(() => {
    const tabs = this.popularCategoryTabs();
    return (
      tabs.find((tab) => tab.id === this.selectedPopularCategoryTabId()) ??
      tabs[0] ?? { id: '', label: '', categories: [] }
    );
  });

  /** Nothing to show means no heading over an empty grid. */
  protected readonly hasPopularCategories = computed(
    () => this.selectedPopularCategoryTab().categories.length > 0,
  );

  protected readonly popularCategoryTotalSlides = computed(() =>
    Math.max(
      1,
      this.selectedPopularCategoryTab().categories.length - this.popularCardsPerSlide() + 1,
    ),
  );

  protected readonly popularCategorySlideIndexes = computed(() =>
    Array.from({ length: this.popularCategoryTotalSlides() }, (_, index) => index),
  );

  /**
   * The stored index, held inside the range the current width allows.
   *
   * Slides are `categories - perSlide + 1`, so a wider viewport shows more per slide and therefore
   * has fewer of them. Resizing a phone-width window up to desktop would otherwise leave the raw
   * index pointing past the last slide, and the slice would come back short — a row with empty
   * space at the end of it.
   */
  protected readonly activePopularCategorySlide = computed(() =>
    Math.min(this.currentPopularCategorySlide(), this.popularCategoryTotalSlides() - 1),
  );

  protected readonly visiblePopularCategories = computed(() => {
    const start = this.activePopularCategorySlide();
    return this.selectedPopularCategoryTab().categories.slice(
      start,
      start + this.popularCardsPerSlide(),
    );
  });

  protected selectTab(tabId: string): void {
    this.selectedTabId.set(tabId);
    this.currentSlide.set(0);
  }

  protected getDealThumbUrl(type: DealThumbType): string {
    return type === 'airpods'
      ? '/images/trending-products/product-thumb1.png'
      : '/images/trending-products/product-thumb2.png';
  }

  protected onDealThumbError(dealKey: string): void {
    this.dealThumbFailedByKey.update((current) => ({ ...current, [dealKey]: true }));
  }

  protected onDealThumbLoad(dealKey: string): void {
    this.dealThumbFailedByKey.update((current) => ({ ...current, [dealKey]: false }));
  }

  protected isDealThumbMissing(dealKey: string): boolean {
    return this.dealThumbFailedByKey()[dealKey] === true;
  }

  protected selectSlide(index: number): void {
    this.currentSlide.set(index);
  }

  protected selectPopularCategoryTab(tabId: string): void {
    this.selectedPopularCategoryTabId.set(tabId);
    this.currentPopularCategorySlide.set(0);
    this.restartPopularCategoryAutoplay();
  }

  protected selectPopularCategorySlide(index: number): void {
    this.currentPopularCategorySlide.set(index);
    this.restartPopularCategoryAutoplay();
  }

  protected selectTrendingCouponTab(tabId: string): void {
    this.selectedTrendingCouponTabId.set(tabId);
  }

  protected selectPremiumCollectionTab(tabId: string): void {
    this.selectedPremiumCollectionTabId.set(tabId);
  }

  protected selectJournalTab(tabId: string): void {
    this.selectedJournalTabId.set(tabId);
  }

  protected toggleFaq(index: number): void {
    this.expandedFaqIndex.update((current) => (current === index ? null : index));
  }

  protected selectHeroSlide(index: number): void {
    if (this.heroSlides().length === 0 || index < 0 || index >= this.heroSlides().length) {
      return;
    }

    const current = this.currentHeroSlideIndex();
    const last = this.heroSlides().length - 1;
    const direction: 'next' | 'prev' =
      current === last && index === 0
        ? 'next'
        : current === 0 && index === last
          ? 'prev'
          : index >= current
            ? 'next'
            : 'prev';

    this.triggerHeroSlideAnimation(direction);
    this.currentHeroSlideIndex.set(index);
    this.restartHeroAutoplay();
  }

  /**
   * Previous / next arrow.
   *
   * Direction comes from the button pressed rather than from comparing indexes, so wrapping from
   * the last slide to the first still animates forwards (and back again) the way the click implies.
   */
  protected stepHeroSlide(delta: 1 | -1): void {
    const total = this.heroSlides().length;
    if (total < 2) return;

    this.triggerHeroSlideAnimation(delta === 1 ? 'next' : 'prev');
    this.currentHeroSlideIndex.update((index) => (index + delta + total) % total);
    this.restartHeroAutoplay();
  }

  protected pauseHeroAutoplay(): void {
    this.stopHeroAutoplay();
  }

  protected resumeHeroAutoplay(): void {
    this.startHeroAutoplay();
  }

  protected toLinkHref(link: string): string {
    if (!link || link === '#' || /^https?:\/\//i.test(link) || link.startsWith('#')) {
      return link || '#';
    }

    return link.startsWith('/') ? link : `/${link}`;
  }

  /** True for links `toLinkHref` resolved to an on-site route, so they can use routerLink. */
  private isInternalLink(link: string): boolean {
    return link.startsWith('/');
  }

  protected hasHeroCta(slide: HeroSlide): boolean {
    return slide.ctaLabel.length > 0 && slide.ctaLink.length > 0;
  }

  protected hasHeroStorePanel(slide: HeroSlide): boolean {
    return (
      slide.storesTitle.length > 0 || slide.storesSubtitle.length > 0 || slide.badges.length > 0
    );
  }

  // Logo load failures on the hero badges, the brand directory and the deals brand used to be
  // tracked here and swapped for a favicon or a bag icon. app-store-logo now handles a failed
  // image itself, painting the store's name on white in the same slot.

  // Per-section static flags. Each entry is `true` while the section renders hardcoded data.
  // When a section is wired to the admin panel, replace its value with a data-driven check
  // (e.g. `this.someApiSignal().length === 0`) and its red dot disappears automatically once
  // data loads. Being a computed, any signal read here makes the marker reactive.
  protected readonly staticSectionFlags = computed<Record<StaticHomeSection, boolean>>(() => ({
    ai: true,
    deals: this.dealTabs().length === 0,
    sponsoredBrands: this.sponsoredBrands().length === 0,
    popularCategories: true,
    trendingCoupons: this.trendingCouponTabs().length === 0,
    // Wired to the DB: the dot clears automatically once the latest per-store products load.
    premiumCollection: this.premiumCollectionTabs().length === 0,
    // Wired to the DB: the dot clears automatically once real stores load.
    partneredStores: this.partneredStores().length === 0,
    bigSaleBanner: true,
    journal: true,
    faq: this.homeFaqs().length === 0,
  }));

  protected isStaticSection(section: StaticHomeSection): boolean {
    return STATIC_SECTION_MARKERS_ENABLED && this.staticSectionFlags()[section];
  }

  constructor() {
    this.applyHomeSeo();
    this.loadHomePageSettings();
    this.loadTopStores();
    this.loadPartneredStores();
    this.loadNewCollection();
    this.loadTrendingCoupons();
    this.loadPopularCategories();
    this.loadTodayDeals();
    this.startHeroAutoplay();
    this.startPopularCategoryAutoplay();
    this.trackViewportWidth();
  }

  ngOnDestroy(): void {
    this.stopHeroAutoplay();
    this.stopPopularCategoryAutoplay();
    this.stopTrackingViewportWidth();
  }

  /**
   * Feeds `popularCardsPerSlide`, which has to follow the same breakpoints as the category grid's
   * column count. Browser-only: during SSR there is no viewport, and the signal's own default
   * covers that case.
   */
  private trackViewportWidth(): void {
    if (!this.isBrowser) return;
    this.viewportWidth.set(window.innerWidth);
    this.onViewportResize = () => this.viewportWidth.set(window.innerWidth);
    window.addEventListener('resize', this.onViewportResize, { passive: true });
  }

  private stopTrackingViewportWidth(): void {
    if (!this.isBrowser || !this.onViewportResize) return;
    window.removeEventListener('resize', this.onViewportResize);
    this.onViewportResize = null;
  }

  /**
   * Home-page SEO. Before this the site's most-linked page set no <title>, description, canonical
   * or structured data at all (and rendered no <h1> — the hero headline is now one).
   *
   * The copy is deliberately static: the hero slides are admin-authored marketing lines that rotate
   * on a timer, so they are not a stable page title. Nothing here waits on data, which makes the
   * constructor the right place — the tags are in the DOM before SSR serialises it.
   */
  private applyHomeSeo(): void {
    const title = 'SavingArena | Verified Coupons, Promo Codes & Deals';
    const description =
      'Save at thousands of stores with verified coupon codes, promo codes and deals. SavingArena hand-checks every offer so you only use codes that work.';

    this.seo.apply({ title, description, canonicalPath: '/' });

    // Organization + WebSite schema are emitted once from the app shell (app.ts); the home page only
    // adds its own WebPage node.
    this.seo.setJsonLd([
      webPageSchema({
        origin: this.seo.origin,
        url: this.seo.absolute('/'),
        name: title,
        description,
      }),
    ]);
  }

  private startHeroAutoplay(): void {
    if (this.heroAutoplayTimer !== null || this.heroSlides().length < 2) {
      return;
    }

    this.heroAutoplayTimer = setInterval(() => {
      this.triggerHeroSlideAnimation('next');
      this.currentHeroSlideIndex.update((index) => (index + 1) % this.heroSlides().length);
    }, 5500);
  }

  private stopHeroAutoplay(): void {
    if (this.heroAutoplayTimer === null) {
      return;
    }

    clearInterval(this.heroAutoplayTimer);
    this.heroAutoplayTimer = null;
  }

  private restartHeroAutoplay(): void {
    this.stopHeroAutoplay();
    this.startHeroAutoplay();
  }

  private startPopularCategoryAutoplay(): void {
    if (this.popularCategoryAutoplayTimer !== null || this.popularCategoryTotalSlides() < 2) {
      return;
    }

    this.popularCategoryAutoplayTimer = setInterval(() => {
      const totalSlides = this.popularCategoryTotalSlides();
      // Steps on from the clamped value, so after a resize shrinks the slide count the carousel
      // carries on from the slide actually on screen instead of jumping.
      const next = (this.activePopularCategorySlide() + 1) % totalSlides;
      this.currentPopularCategorySlide.set(next);
    }, 5000);
  }

  private stopPopularCategoryAutoplay(): void {
    if (this.popularCategoryAutoplayTimer === null) {
      return;
    }

    clearInterval(this.popularCategoryAutoplayTimer);
    this.popularCategoryAutoplayTimer = null;
  }

  private restartPopularCategoryAutoplay(): void {
    this.stopPopularCategoryAutoplay();
    this.startPopularCategoryAutoplay();
  }

  private escapeRegExp(value: string): string {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  private triggerHeroSlideAnimation(direction: 'next' | 'prev'): void {
    this.heroSlideDirection.set(direction);
    this.heroSlideAnimate.set(false);

    setTimeout(() => {
      this.heroSlideAnimate.set(true);
    });
  }

  // Loads a "{Event} Top Stores" block for every currently-running event (or the single next
  // upcoming one when none are live), each with its own heading. As dates roll over, a fresh
  // visit to the home page recomputes the set on its own.
  private loadTopStores(): void {
    this.api
      .getEvents({ page: 1, limit: 100 })
      .pipe(
        map((response) => this.pickFeaturedEvents(response.data)),
        // The first picked event (soonest-ending live, else next upcoming) names the BIG SALE banner.
        tap((events) => this.setFeaturedEvent(events[0] ?? null)),
        switchMap((events) =>
          events.length > 0
            ? forkJoin(
                events.map((event) =>
                  this.api
                    .getEventTopStores(event.slug || event.id)
                    .pipe(catchError(() => of(null))),
                ),
              )
            : of([]),
        ),
        catchError(() => of([])),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((responses) => {
        const sections = responses
          .map((response) => this.toTopStoresSection(response?.data ?? null))
          .filter((section): section is TopStoresSection => section !== null);

        this.topStoresSections.set(sections);
      });
  }

  private setFeaturedEvent(event: FrontendEvent | null): void {
    if (!event) {
      this.featuredEventName.set('');
      this.featuredEventPath.set(null);
      return;
    }

    const slug = String(event.slug || event.id || '').trim();
    this.featuredEventName.set(event.name?.trim() ?? '');
    this.featuredEventPath.set(slug ? `/event/${slug}` : null);
  }

  // Every currently-running event (started, not yet ended), soonest-ending first. If none is
  // live, fall back to the single nearest upcoming event (soonest start in the future).
  private pickFeaturedEvents(events: FrontendEvent[]): FrontendEvent[] {
    const now = Date.now();
    const dated = events
      .map((event) => ({
        event,
        start: this.parseEventDateMs(event.startDate),
        end: this.parseEventDateMs(event.endDate),
      }))
      .filter(
        (item): item is { event: FrontendEvent; start: number; end: number | null } =>
          item.start !== null,
      );

    const active = dated
      .filter((item) => item.start <= now && (item.end === null || item.end >= now))
      .sort((a, b) => (a.end ?? Number.MAX_SAFE_INTEGER) - (b.end ?? Number.MAX_SAFE_INTEGER));

    if (active.length > 0) {
      return active.map((item) => item.event);
    }

    const upcoming = dated.filter((item) => item.start > now).sort((a, b) => a.start - b.start);

    return upcoming.length > 0 ? [upcoming[0].event] : [];
  }

  private toTopStoresSection(event: FrontendEvent | null): TopStoresSection | null {
    if (!event) {
      return null;
    }

    const stores = this.mapTopStores(event.data);

    if (stores.length === 0) {
      return null;
    }

    const name = event.name?.trim() ?? '';

    // The calendar year, not the event's own year, and read at render time so it rolls over on its
    // own. The event page does the opposite deliberately: there the year belongs to the event, so
    // an event dated 2027 says 2027 even while we are in 2026. Here the heading is about what the
    // home page is showing right now.
    //
    // Stored event names carry no year, so this cannot double one up; the guard covers a name that
    // someone later types one into anyway.
    const year = new Date().getFullYear();
    const withYear = name.includes(String(year)) ? name : `${name} ${year}`;

    return {
      id: event.id,
      heading: name ? `${withYear} Top Stores` : `Top Stores ${year}`,
      stores,
    };
  }

  // The event detail payload nests its stores under `data.topStoresFeatured` (the circular-logo
  // row). Fall back to `data.topStores` when featured is empty so a partly-configured event still
  // populates the row.
  private mapTopStores(data: unknown): TopStoreLogo[] {
    if (!data || typeof data !== 'object' || Array.isArray(data)) {
      return [];
    }

    const record = data as Record<string, unknown>;
    const featured = Array.isArray(record['topStoresFeatured']) ? record['topStoresFeatured'] : [];
    const source =
      featured.length > 0
        ? featured
        : Array.isArray(record['topStores'])
          ? record['topStores']
          : [];

    return (source as unknown[])
      .filter((item): item is Record<string, unknown> => !!item && typeof item === 'object')
      .map((store) => this.toTopStoreLogo(store))
      .filter((store) => store.logoUrl.length > 0);
  }

  private toTopStoreLogo(store: Record<string, unknown>): TopStoreLogo {
    const name = typeof store['name'] === 'string' ? store['name'] : '';
    const slug = typeof store['slug'] === 'string' ? store['slug'].trim() : '';
    const logoPath =
      typeof store['logoImagePath'] === 'string' ? store['logoImagePath'].trim() : '';
    const id = typeof store['id'] === 'string' ? store['id'] : slug || name;

    return {
      id,
      name,
      logoUrl: logoPath ? this.toAssetUrl(logoPath) : '',
      path: slug ? `/${slug}` : '#',
    };
  }

  private parseEventDateMs(value?: string): number | null {
    if (!value) {
      return null;
    }

    const ms = new Date(value).getTime();
    return Number.isNaN(ms) ? null : ms;
  }

  private loadPartneredStores(): void {
    // Newest published stores first — by publishedAt, which records when a person made the store
    // live, not createdAt, which records when a network sync first saw it.
    //
    // Those are months apart in this catalogue: every store this section used to show was created
    // in one bulk import on 2025-11-26 and published long afterwards, so the section sat frozen on
    // the tail of that import and publishing anything new never moved it.
    //
    // The backend returns only stores carrying a publishedAt when asked for this order; we
    // de-duplicate same-brand records before taking the top N.
    this.api
      .getStores({
        sortBy: 'publishedAt',
        sortOrder: 'desc',
        page: 1,
        limit: PARTNERED_STORES_FETCH_LIMIT,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.partneredStores.set(this.buildPartneredStores(response.data));
        },
        error: () => undefined,
      });
  }

  // Collapse same-brand records into one card. The DB stores a brand as several records
  // (region/network variants) and coupons attach to a specific record, so a brand's counts
  // can be split across its records — summing them keeps the displayed totals accurate. The
  // newest record for the brand is kept as the visual representative (logo/name).
  private buildPartneredStores(stores: FrontendStore[]): PartneredStoreItem[] {
    interface BrandAccumulator {
      representative: FrontendStore;
      couponsCount: number;
      dealsCount: number;
    }

    const order: string[] = [];
    const brands = new Map<string, BrandAccumulator>();

    for (const store of stores) {
      const key = (store.domain?.trim() || store.name).trim().toLowerCase();

      if (!key) {
        continue;
      }

      const coupons = typeof store.couponsCount === 'number' ? store.couponsCount : 0;
      const deals = typeof store.dealsCount === 'number' ? store.dealsCount : 0;
      const existing = brands.get(key);

      if (existing) {
        existing.couponsCount += coupons;
        existing.dealsCount += deals;
      } else {
        brands.set(key, { representative: store, couponsCount: coupons, dealsCount: deals });
        order.push(key);
      }
    }

    return order.slice(0, PARTNERED_STORES_LIMIT).map((key) => {
      const brand = brands.get(key)!;
      return this.toPartneredStore(brand.representative, brand.couponsCount, brand.dealsCount);
    });
  }

  private toPartneredStore(
    store: FrontendStore,
    couponsCount: number,
    dealsCount: number,
  ): PartneredStoreItem {
    const logoPath = store.logoImagePath?.trim() ?? '';
    const slug = store.slug?.trim() ?? '';

    return {
      name: store.name,
      logoUrl: logoPath ? this.toAssetUrl(logoPath) : this.getDomainLogoUrl(store),
      couponsAndDealsCount: formatOfferCount(couponsCount, dealsCount),
      path: slug ? `/${slug}` : '#',
    };
  }

  private loadNewCollection(): void {
    // Tabs are admin-flagged categories; each maps to the newest per-store products in it.
    this.api
      .getNewCollection(NEW_COLLECTION_LIMIT)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          const categories = response.data?.categories ?? [];
          const productsByCategory = response.data?.products ?? {};

          const byCategory: Record<string, PremiumCollectionItem[]> = {};
          for (const category of categories) {
            byCategory[category.id] = (productsByCategory[category.id] ?? []).map((product) =>
              this.toPremiumCollectionItem(product),
            );
          }

          this.premiumCollectionByCategory.set(byCategory);
          this.premiumCollectionTabs.set(
            categories.map((category) => ({
              id: category.id,
              label: category.name,
              slug: category.slug ?? '',
            })),
          );
          this.selectedPremiumCollectionTabId.set(categories[0]?.id ?? '');
        },
        error: () => undefined,
      });
  }

  private loadTrendingCoupons(): void {
    // Tabs are admin-flagged coupon categories; each maps to that category's latest active coupons.
    this.api
      .getTrendingCoupons(TRENDING_COUPONS_LIMIT)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          const categories = response.data?.categories ?? [];
          const couponsByCategory = response.data?.coupons ?? {};

          const byTab: Record<string, CouponItem[]> = {};
          for (const category of categories) {
            byTab[category.id] = (couponsByCategory[category.id] ?? []).map((coupon) =>
              this.toTrendingCouponItem(coupon),
            );
          }

          this.trendingCouponsByTab.set(byTab);
          this.trendingCouponTabs.set(
            categories.map((category) => ({
              id: category.id,
              label: category.name,
              // Carried so "View More <tab> Coupons" can link to that category's page.
              slug: category.slug ?? '',
            })),
          );
          this.selectedTrendingCouponTabId.set(categories[0]?.id ?? '');
        },
        error: () => undefined,
      });
  }

  private loadPopularCategories(): void {
    // The "Coupons & Offers" tab is admin-flagged coupon categories; each links to its category page.
    this.api
      .getPopularCategories()
      .pipe(
        catchError(() => of({ success: false, data: [] as FrontendPopularCategory[] })),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((response) => {
        const items = (response.data ?? [])
          .map((category) => this.toPopularCategoryItem(category))
          .filter((item) => item.label.length > 0);
        this.popularCouponCategories.set(items);
      });

    // The "Products & Deals" tab is the categories that currently have a deals page; each links to
    // /collection/<slug>. The endpoint only returns qualifying categories, so no tile 404s.
    this.api
      .getDealsCategories(12)
      .pipe(
        catchError(() => of({ success: false, data: [] as FrontendDealCategoryListItem[] })),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((response) => {
        const items = (response.data ?? [])
          .map((category) => this.toPopularDealCategoryItem(category))
          .filter((item) => item.label.length > 0 && Boolean(item.path));
        this.popularDealCategories.set(items);
      });
  }

  private toPopularDealCategoryItem(category: FrontendDealCategoryListItem): PopularCategoryItem {
    const slug = (category.slug || '').trim();
    // The admin's category icon when there is one, else the category's newest product image — so a
    // tile is never an empty circle.
    const imagePath =
      (category.iconImagePath || '').trim() || (category.productImagePath || '').trim();

    return {
      label: (category.name || '').trim(),
      slug,
      path: slug ? `/collection/${slug}` : '',
      imageUrl: imagePath ? this.toAssetUrl(imagePath) : '',
      // No product image either. The deals-category endpoint carries no icon code, so the mark here
      // is the category's initials rather than an icon — still a mark, never a blank circle.
      iconGlyph: imagePath ? '' : categoryInitials(category.name),
    };
  }

  private toPopularCategoryItem(category: FrontendPopularCategory): PopularCategoryItem {
    const iconType = (category.iconType || '').trim();
    const imagePath = (category.iconImagePath || '').trim();
    const iconCode = (category.iconCode || '').trim();

    const slug = (category.slug || '').trim();

    return {
      label: (category.name || '').trim(),
      slug,
      path: slug ? `/categories/${slug}` : '',
      imageUrl: iconType === 'image' && imagePath ? this.toAssetUrl(imagePath) : '',
      // Not the raw snippet. Every category's icon code is a Font Awesome class and the storefront
      // ships no icon font, so passing it through rendered an empty <i> — see category-icon.ts.
      iconGlyph: iconType === 'code' ? categoryFallbackMark(iconCode, category.name) : '',
    };
  }

  /**
   * "Get Deal" / "Reveal Code" on a trending coupon.
   *
   * Two destinations at once:
   *  - a NEW tab gets the store page at its offer URL (`/<store>/coupon/<id>`), which the store page
   *    turns into the offer popup — so the code is on screen and the page can be shared;
   *  - THIS tab follows the `/go/:store/:id` redirect to the merchant.
   *
   * Order matters. The new tab is opened first, while we are still inside the click handler and the
   * browser still counts this as a user gesture — set `location` first and the pending `window.open`
   * is discarded as the page unloads. Opening exactly one tab per click also keeps popup blockers
   * satisfied.
   *
   * The redirect increments the coupon's Hits counter server-side, so nothing is recorded here.
   */
  protected openTrendingCoupon(coupon: CouponItem, event: Event): void {
    event.preventDefault();
    if (!this.isBrowser || !coupon.id || !coupon.storeSlug) return;

    this.affiliate.openCoupon({
      storeSlug: coupon.storeSlug,
      couponId: coupon.id,
      store: coupon.store || coupon.storeSlug,
      kind: 'trending-coupon',
    });
  }

  private toTrendingCouponItem(coupon: FrontendCoupon): CouponItem {
    const store = coupon.store;
    const logoPath = store?.logoImagePath?.trim() ?? '';
    const code = coupon.couponCode?.trim() ?? '';
    const expires =
      coupon.endDateText?.trim() || this.formatCouponExpiry(coupon.endDate) || 'On Going';

    const storeSlug = store?.slug?.trim() ?? '';

    return {
      id: String(coupon.id ?? ''),
      store: store?.name?.trim() || 'Store',
      storeSlug,
      storePath: storeSlug ? `/${storeSlug}` : '',
      logoUrl: logoPath ? this.toAssetUrl(logoPath) : store ? this.getDomainLogoUrl(store) : '',
      title: coupon.name?.trim() || this.stripHtml(coupon.detail) || 'View offer',
      expires,
      ctaType: code ? 'code' : 'deal',
      codeSuffix: code ? code.slice(-3) : undefined,
    };
  }

  private formatCouponExpiry(value?: string): string {
    if (!value) {
      return '';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    return date.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
  }

  private loadTodayDeals(): void {
    // Tabs are admin-flagged stores; each shows that store's latest products.
    this.api
      .getTodayTopDeals(TODAY_DEALS_LIMIT)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          const stores = response.data?.stores ?? [];
          const productsByStore = response.data?.products ?? {};

          const tabs = stores.map((store) =>
            this.toDealTab(store, productsByStore[store.id] ?? []),
          );

          this.dealTabs.set(tabs);
          this.selectedTabId.set(tabs[0]?.id ?? '');
          this.currentSlide.set(0);
        },
        error: () => undefined,
      });
  }

  private toDealTab(store: FrontendDealStore, products: FrontendProduct[]): DealTab {
    const logoPath = store.logoImagePath?.trim() ?? '';
    const slug = store.slug?.trim() ?? '';
    // Empty when the store has too few products for a deals page — the link is then hidden rather
    // than pointing at a 404.
    const dealsKey = store.dealsStoreKey?.trim() ?? '';

    return {
      id: store.id,
      label: store.name?.trim() || 'Store',
      presentedBy: (store.name?.trim() || 'Store').toUpperCase(),
      couponsPath: slug ? `/${slug}` : '',
      dealsPath: dealsKey ? this.dealsStorePath(dealsKey, store.dealsStoreCategorySlug) : '',
      logoUrl: logoPath
        ? this.toAssetUrl(logoPath)
        : this.getDomainLogoUrl(store as unknown as ApiRecord),
      deals: products.map((product) => this.toDealItem(product)),
    };
  }

  private toDealItem(product: FrontendProduct): DealItem {
    const imagePath = product.thumbnailImagePath?.trim() ?? '';
    const isExternal = product.isExternal === true;
    const oldPrice = product.oldPrice?.trim() ?? '';
    const productSlug = product.slug?.trim() ?? '';

    return {
      title: (product.title?.trim() || product.name?.trim() || '').trim(),
      discount: this.computeDealDiscount(product.oldPrice, product.newPrice),
      category: product.category?.name?.trim() ?? '',
      imageUrl: imagePath ? this.toAssetUrl(imagePath) : '',
      hoverImageUrl: this.toHoverImageUrl(product, imagePath),
      // Same formatting as the New Collection cards, so both grids render prices identically.
      price: this.formatProductPrice(product.newPrice),
      oldPrice: oldPrice ? this.formatProductPrice(oldPrice) : '',
      isExternal,
      id: String(product.id ?? ''),
      detailPath: productSlug ? `/${productSlug}` : '',
    };
  }

  // "27% OFF" when there's a genuine markdown (oldPrice > newPrice); empty otherwise (badge hidden).
  private computeDealDiscount(oldPrice?: string, newPrice?: string): string {
    const oldValue = this.parseDealPrice(oldPrice);
    const newValue = this.parseDealPrice(newPrice);

    if (oldValue > 0 && newValue > 0 && newValue < oldValue) {
      const pct = Math.round(((oldValue - newValue) / oldValue) * 100);
      if (pct > 0) {
        return `${pct}% OFF`;
      }
    }
    return '';
  }

  private parseDealPrice(value?: string): number {
    const num = parseFloat(String(value ?? '').replace(/[^0-9.]/g, ''));
    return Number.isFinite(num) ? num : 0;
  }

  private toPremiumCollectionItem(product: FrontendProduct): PremiumCollectionItem {
    const store = product.store;
    const logoPath = store?.logoImagePath?.trim() ?? '';
    const imagePath = product.thumbnailImagePath?.trim() ?? '';
    const oldPrice = product.oldPrice?.trim() ?? '';
    const storeSlug = store?.slug?.trim() ?? '';

    return {
      store: store?.name?.trim() || 'Store',
      storeLogoUrl: logoPath
        ? this.toAssetUrl(logoPath)
        : store
          ? this.getDomainLogoUrl(store)
          : undefined,
      storePath: storeSlug ? `/${storeSlug}` : '#',
      id: String(product.id ?? ''),
      detailPath: product.slug?.trim() ? `/${product.slug.trim()}` : '',
      category: product.category?.name?.trim() ?? '',
      title: product.title?.trim() || product.name?.trim() || '',
      description: this.stripHtml(product.description),
      isExternal: product.isExternal === true,
      price: this.formatProductPrice(product.newPrice),
      oldPrice: oldPrice ? this.formatProductPrice(oldPrice) : undefined,
      imageUrl: imagePath ? this.toAssetUrl(imagePath) : '',
      hoverImageUrl: this.toHoverImageUrl(product, imagePath),
    };
  }

  // First gallery angle that genuinely differs from the thumbnail; '' when the feed gave only one.
  private toHoverImageUrl(product: FrontendProduct, mainImagePath: string): string {
    const alternate = (product.galleryImagePaths ?? []).find(
      (path) => path && path !== mainImagePath,
    );

    return alternate ? this.toAssetUrl(alternate) : '';
  }

  // Product descriptions may carry admin-authored HTML; the card shows plain text only.
  private stripHtml(value?: string): string {
    return (value ?? '')
      .replace(/<[^>]*>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  // Product prices are stored as free-form strings; prefix a "$" only when the value starts with a
  // digit so admin-entered symbols (€, £, "Free") pass through unchanged.
  private formatProductPrice(value?: string): string {
    const raw = (value ?? '').trim();

    if (!raw) {
      return '';
    }

    return /^[0-9]/.test(raw) ? `$${raw}` : raw;
  }

  private loadHomePageSettings(): void {
    this.api
      .getHome()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          const slides = this.mapHomeBannerSlides(response.data);
          const brandDirectory = this.mapBrandDirectory(response.data);

          this.brandDirectoryTitle.set(brandDirectory.title);
          this.brandDirectoryStores.set(brandDirectory.stores);

          this.sponsoredBrandsTitle.set(response.data?.sponsoredBrandsSectionTitle?.trim() ?? '');
          this.sponsoredBrands.set(this.mapSponsoredBrands(response.data));

          this.homeFaqTitle.set(response.data?.faqSectionTitle?.trim() ?? '');
          this.homeFaqs.set(this.mapHomeFaqs(response.data));

          if (slides.length === 0) {
            return;
          }

          this.heroSlides.set(slides);
          this.currentHeroSlideIndex.set(0);
          // The first slide's art is this page's Largest Contentful Paint and it arrives as a CSS
          // background, which the browser cannot see until it has parsed the stylesheet and laid the
          // box out. Preloading it from the rendered head starts that download immediately.
          this.seo.preloadImage(slides[0]?.imageUrl ?? '');
          this.restartHeroAutoplay();
        },
        error: () => undefined,
      });
  }

  private mapHomeFaqs(settings: FrontendHomeSettings | null): FaqItem[] {
    if (!settings || !Array.isArray(settings.faqs)) {
      return [];
    }

    return (
      settings.faqs
        .map((item) => this.toRecord(item))
        .filter((item): item is ApiRecord => item !== null)
        .map((item) => ({
          question: this.getString(item, ['question']).trim(),
          answer: typeof item['answer'] === 'string' ? item['answer'] : '',
        }))
        // Admin rows can be saved blank; only show FAQs that actually have a question.
        .filter((faq) => faq.question.length > 0)
    );
  }

  private mapSponsoredBrands(settings: FrontendHomeSettings | null): SponsoredBrandCard[] {
    if (!settings || !Array.isArray(settings.sponsoredBrands)) {
      return [];
    }

    return settings.sponsoredBrands
      .map((brand): SponsoredBrandCard | null => {
        const store = brand.store;
        const imagePath = brand.backgroundImagePath?.trim() ?? '';
        const title = brand.title?.trim() ?? '';

        // The backend already drops half-filled rows, but the same rule is enforced here so the
        // section can never render a blank placeholder card if that filter ever changes: a brand
        // without a store, a headline or an image is not a card.
        if (!store || !title || !imagePath) {
          return null;
        }

        const logoPath = store.logoImagePath?.trim() ?? '';
        const slug = store.slug?.trim() ?? '';

        return {
          title: brand.title?.trim() ?? '',
          // Admin authors this as rich text, so it arrives wrapped in <p>…</p>. The card
          // interpolates it as text, which printed the tags verbatim — strip them, the same way
          // the product and collection cards handle their descriptions.
          description: this.stripHtml(brand.description),
          imageUrl: imagePath ? this.toAssetUrl(imagePath) : '',
          storeName: store.name?.trim() || 'Store',
          storeLogoUrl: logoPath ? this.toAssetUrl(logoPath) : this.getDomainLogoUrl(store),
          storePath: slug ? `/${slug}` : '#',
        };
      })
      .filter((brand): brand is SponsoredBrandCard => brand !== null);
  }

  private mapBrandDirectory(settings: FrontendHomeSettings | null): {
    title: string;
    stores: BrandDirectoryStore[];
  } {
    if (!settings) {
      return { title: '', stores: [] };
    }

    return {
      title: settings.topStoresSectionTitle?.trim() ?? '',
      stores: Array.isArray(settings.topStores)
        ? settings.topStores
            .map((store) => this.toRecord(store))
            .filter((store): store is ApiRecord => store !== null)
            .map((store) => this.mapBrandDirectoryStore(store))
            .filter((store): store is BrandDirectoryStore => store !== null)
        : [],
    };
  }

  private mapBrandDirectoryStore(store: ApiRecord): BrandDirectoryStore | null {
    const name = this.getString(store, ['name', 'title', 'label']);
    const domain = this.normalizeDomain(
      this.getString(store, ['domain']) ||
        this.extractHostname(this.getString(store, ['storeUrl', 'href', 'url', 'link'])),
    );
    const logoPath = this.getString(store, [
      'logoUrl',
      'logoImagePath',
      'logoPath',
      'imageUrl',
      'imagePath',
    ]);
    const coverPath = this.getString(store, [
      'coverUrl',
      'coverImagePath',
      'bannerImagePath',
      'thumbnailImagePath',
    ]);
    const coverBackgroundColor =
      this.getString(store, ['topPickImageBackgroundColor']) || '#f6f6f9';

    if (!name) {
      return null;
    }

    return {
      name,
      // Link to the store's own page on the storefront (its slug), not its external website —
      // these are all SavingArena stores, so clicking a brand card opens /{storeSlug}.
      href: this.toLinkHref(this.slugToPath(this.getString(store, ['slug']))),
      domain,
      logoUrl: logoPath ? this.toAssetUrl(logoPath) : this.getDomainLogoUrl(store),
      coverUrl: coverPath ? this.toAssetUrl(coverPath) : '',
      coverBackgroundColor,
      description: this.getPlainTextString(store, [
        'topPickShortDescription',
        'description',
        'shortDescription',
        'summary',
      ]),
    };
  }

  private mapHomeBannerSlides(settings: FrontendHomeSettings | null): HeroSlide[] {
    if (!settings || !Array.isArray(settings.bannerSlides)) {
      return [];
    }

    return settings.bannerSlides
      .map((slide) => this.toRecord(slide))
      .filter((slide): slide is ApiRecord => slide !== null)
      .map((slide) => this.mapHomeBannerSlide(slide))
      .filter((slide) => this.isUsableHeroSlide(slide));
  }

  private mapHomeBannerSlide(slide: ApiRecord): HeroSlide {
    const title = this.getString(slide, ['title', 'heading', 'headline', 'bannerTitle']);
    const description = this.getString(slide, [
      'description',
      'subtitle',
      'subTitle',
      'summary',
      'bannerDescription',
    ]);
    const imagePath = this.getString(slide, [
      'imageUrl',
      'imagePath',
      'bannerImagePath',
      'backgroundImagePath',
      'desktopImagePath',
    ]);
    const badges = this.mapSlideBadges(slide);

    return {
      titleHtml: title,
      titleLines: this.getStringArray(slide, ['titleLines', 'headingLines']).length
        ? this.getStringArray(slide, ['titleLines', 'headingLines'])
        : this.splitHeroText(title),
      highlightWord: this.getString(slide, ['highlightWord', 'highlightText', 'accentWord']) || '',
      descriptionLines: this.getStringArray(slide, ['descriptionLines', 'subtitleLines']).length
        ? this.getStringArray(slide, ['descriptionLines', 'subtitleLines'])
        : this.splitHeroText(description),
      ctaLabel: this.getString(slide, ['ctaLabel', 'buttonText', 'buttonLabel']),
      ctaLink: this.getString(slide, ['ctaLink', 'buttonLink', 'buttonUrl', 'url']),
      storesTitle: this.getPlainTextString(slide, [
        'storesTitle',
        'partnersTitle',
        'badgesTitle',
        'cardTitle',
      ]),
      storesSubtitle:
        this.getPlainTextString(slide, [
          'storesSubtitle',
          'partnersSubtitle',
          'badgesSubtitle',
          'cardDescription',
        ]) || '',
      theme: this.toHeroTheme(this.getString(slide, ['theme', 'colorTheme'])),
      imageUrl: imagePath ? this.toAssetUrl(imagePath) : '',
      badges,
    };
  }

  private isUsableHeroSlide(slide: HeroSlide): boolean {
    return (
      slide.titleHtml.length > 0 ||
      slide.titleLines.length > 0 ||
      slide.descriptionLines.length > 0 ||
      slide.imageUrl.length > 0 ||
      this.hasHeroCta(slide) ||
      this.hasHeroStorePanel(slide)
    );
  }

  private mapSlideBadges(slide: ApiRecord): HeroBadge[] {
    for (const key of ['badges', 'stores', 'brands', 'storeBadges', 'brandBadges']) {
      const value = slide[key];

      if (!Array.isArray(value)) {
        continue;
      }

      return value
        .map((item) => this.toRecord(item))
        .filter((item): item is ApiRecord => item !== null)
        .map((item) => this.mapBadgeRecord(item))
        .filter((badge): badge is HeroBadge => badge !== null)
        .slice(0, 3);
    }

    return [];
  }

  private mapBadgeRecord(record: ApiRecord): HeroBadge | null {
    const alt = this.getString(record, ['alt', 'name', 'title', 'label']);
    const logoPath = this.getString(record, [
      'logoUrl',
      'logoImagePath',
      'logoPath',
      'imageUrl',
      'imagePath',
      'thumbnailImagePath',
    ]);
    const domainLogoUrl = this.getDomainLogoUrl(record);

    if (!alt || (!logoPath && !domainLogoUrl)) {
      return null;
    }

    // Order matters. The backend serializes every banner store with `storeUrl` and
    // `affiliateLink` populated, so those must rank *below* the slug or each badge would
    // navigate off-site to the merchant instead of to our own store page. An explicit
    // `href`/`url`/`link` still wins, since that is an admin-authored override.
    const href = this.toLinkHref(
      this.getString(record, ['href', 'url', 'link']) ||
        this.slugToPath(this.getString(record, ['slug'])) ||
        this.getString(record, ['storeUrl', 'affiliateLink']),
    );

    return {
      alt,
      href,
      path: this.isInternalLink(href) ? href : null,
      logoUrl: logoPath ? this.toAssetUrl(logoPath) : domainLogoUrl,
    };
  }

  private getDomainLogoUrl(record: ApiRecord): string {
    const domain = this.normalizeDomain(
      this.getString(record, ['domain']) ||
        this.extractHostname(this.getString(record, ['storeUrl', 'href', 'url', 'link'])),
    );

    if (!domain) {
      return '';
    }

    return this.getDomainLogoFromDomain(domain);
  }

  private getDomainLogoFromDomain(domain: string): string {
    return domain
      ? `https://www.google.com/s2/favicons?domain=${encodeURIComponent(domain)}&sz=128`
      : '';
  }

  private normalizeDomain(value: string): string {
    const domain = value.trim().toLowerCase();

    if (!domain || domain.includes(' ')) {
      return '';
    }

    return domain
      .replace(/^https?:\/\//, '')
      .replace(/^www\./, '')
      .split('/')[0];
  }

  private extractHostname(value: string): string {
    if (!value) {
      return '';
    }

    try {
      return new URL(value.startsWith('http') ? value : `https://${value}`).hostname;
    } catch {
      return '';
    }
  }

  private getString(record: ApiRecord, keys: string[]): string {
    for (const key of keys) {
      const value = record[key];

      if (typeof value === 'string' && value.trim()) {
        return value.trim();
      }
    }

    return '';
  }

  private getPlainTextString(record: ApiRecord, keys: string[]): string {
    const value = this.getString(record, keys);

    return value ? this.htmlToText(value) : '';
  }

  private getStringArray(record: ApiRecord, keys: string[]): string[] {
    for (const key of keys) {
      const value = record[key];

      if (!Array.isArray(value)) {
        continue;
      }

      const lines = value.filter(
        (item): item is string => typeof item === 'string' && item.trim().length > 0,
      );

      if (lines.length > 0) {
        return lines.map((line) => line.trim()).slice(0, 3);
      }
    }

    return [];
  }

  private splitHeroText(value: string): string[] {
    if (!value) {
      return [];
    }

    const htmlLines = this.htmlToLines(value);

    if (htmlLines.length > 0) {
      return htmlLines.slice(0, 3);
    }

    const explicitLines = value
      .replace(/<br\s*\/?>/gi, '\n')
      .split(/\n+/)
      .map((line) => line.trim())
      .filter(Boolean);

    if (explicitLines.length > 1) {
      return explicitLines.slice(0, 3);
    }

    const commaLines = value
      .split(',')
      .map((line, index, lines) => `${line.trim()}${index < lines.length - 1 ? ',' : ''}`)
      .filter((line) => line !== ',');

    return commaLines.length > 1 ? commaLines.slice(0, 3) : [value];
  }

  private htmlToLines(value: string): string[] {
    return value
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/p>\s*<p[^>]*>/gi, '\n')
      .replace(/<[^>]+>/g, '')
      .split(/\n+/)
      .map((line) => this.decodeHtmlEntities(line).trim())
      .filter(Boolean);
  }

  private htmlToText(value: string): string {
    return this.htmlToLines(value).join(' ');
  }

  private decodeHtmlEntities(value: string): string {
    return value
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'");
  }

  private toHeroTheme(value: string): HeroTheme {
    return value === 'teal' || value === 'slate' || value === 'indigo' ? value : 'teal';
  }

  private toAssetUrl(path: string): string {
    if (/^https?:\/\//i.test(path)) {
      return path;
    }

    const assetBaseUrl = this.apiConfig.assetBaseUrl.replace(/\/$/, '');
    const assetPath = path.startsWith('/') ? path : `/${path}`;

    return `${assetBaseUrl}${assetPath}`;
  }

  private toRecord(value: unknown): ApiRecord | null {
    return value !== null && typeof value === 'object' && !Array.isArray(value)
      ? (value as ApiRecord)
      : null;
  }

  private slugToPath(slug: string): string {
    return slug ? `/${slug}` : '';
  }

  /**
   * A store's deals URL: `/collection/<collection>/<storeKey>`, falling back to the legacy
   * `/deals/<storeKey>` when the API has no collection for it (the deals page then rewrites the
   * address itself).
   */
  private dealsStorePath(storeKey: string, categorySlug?: string): string {
    const collection = (categorySlug ?? '').trim();
    return collection ? `/collection/${collection}/${storeKey}` : `/deals/${storeKey}`;
  }
}


